import { faIdCard } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import PersonBasicInfoModal from '@/components/PersonBasicInfoModal';
import Heading from '@/ui/components/Heading/Heading';
import PageHeaderBar from '@/ui/components/PageHeaderBar';
import formatPersonName from '@/utils/formatPersonName';

import type { Props } from './types';

/**
 * NOTE: This component is business/domain-specific and does not belong in the
 * generic UI primitives library (ui/components). It should be moved to a more
 * appropriate domain-specific location in the codebase.
 */
const PersonHeader = ({ person }: Props) => {
  const [isBasicInfoModalOpen, setIsBasicInfoModalOpen] = useState(false);
  const navigate = useNavigate();

  const closeBasicInfoModal = useCallback(() => setIsBasicInfoModalOpen(false), []);

  const handleProfileClick = useCallback(() => {
    window.scrollTo(0, 0);
    navigate(`/people/${person.id}`);
  }, [navigate, person.id]);

  return (
    <PageHeaderBar>
      <button
        onClick={handleProfileClick}
        className="mp-sensitive rounded-full"
        aria-label={`Go to ${formatPersonName({ person })}'s profile`}>
        {person.profile_image_url ? (
          <img
            className="inline-block h-8 w-8 rounded-full"
            src={person.profile_image_url}
            alt="Profile Picture of Person"
          />
        ) : (
          <span className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-500">
            <span className="font-medium leading-none text-white">
              {person.first_name?.[0]}
              {person.last_name?.[0]}
            </span>
          </span>
        )}
      </button>
      <div className="ml-3 truncate">
        <Heading className="truncate" value={2} text={formatPersonName({ person })} />
      </div>
      <div className="ml-3">
        <button
          title="Show basic info"
          aria-label="Show basic info"
          onClick={() => setIsBasicInfoModalOpen(true)}
          type="button">
          <FontAwesomeIcon icon={faIdCard} />
        </button>
      </div>

      <PersonBasicInfoModal
        person={person}
        open={isBasicInfoModalOpen}
        onClose={closeBasicInfoModal}
      />
    </PageHeaderBar>
  );
};
export default memo(PersonHeader);
